import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { usePreReleaseFeatures } from 'hooks/usePreReleaseFeatures';
import { useTranslation } from 'react-i18next';
import { AUTOMATIONS } from 'queries/query-keys';
import { toastVariant } from 'components/ToastProvider';
import { actions } from 'pages/AutomationEngine/store';
import { PiChatSideLayout } from 'ts-components/PiChat/PiChatSideLayout';
import { useAutomationsSelector } from 'pages/AutomationEngine/store/react';
import { getAutomationId } from 'pages/AutomationEngine/store/selectors';
import { CodeStepAiPanel } from './CodeStepAiPanel';
import type { AutomationStoreLike } from './codeStepSave';
import { readStepConversationMarker } from './conversationMarker';
import {
  isPersistedStepId,
  type CodeStepAiApi,
  type SaveThenOpenRequest,
} from './codeStepAiGate';
import { saveFirstWithStore, type SaveFirstOutcome } from './saveFirst';

const PANEL_RAIL_SELECTOR = ':scope > [data-qa-id="pi-chat-rail"]';

const SAVE_FIRST_TIMEOUT_MS = 60_000;
const CONFIRM_TIMEOUT_MS = 2_000;

const CodeStepAiContext = createContext<CodeStepAiApi | null>(null);

export const useCodeStepAi = () => useContext(CodeStepAiContext);

interface Session {
  key: string;
  automationId: string;
  stepId: string;
  resumeConversationId: string | null;
}

export type CodeStepAiProviderProps = {
  store: AutomationStoreLike;
  children: ReactNode;
};

export const CodeStepAiProvider: FC<CodeStepAiProviderProps> = ({
  store,
  children,
}) => {
  const enabled = usePreReleaseFeatures();
  const rawAutomationId = useAutomationsSelector(getAutomationId);
  const automationId = rawAutomationId ? String(rawAutomationId) : null;
  const [session, setSession] = useState<Session | null>(null);
  const sessionCounterRef = useRef(0);

  if (session && session.automationId !== automationId) setSession(null);

  const canOpen = enabled && automationId !== null;
  const open = useCallback(
    (stepId: string) => {
      if (!canOpen || !automationId || !isPersistedStepId(stepId)) return;
      sessionCounterRef.current += 1;
      const key = `code-step-ai-session-${sessionCounterRef.current}`;
      const resumeConversationId = readStepConversationMarker(
        store.getState(),
        stepId
      );
      setSession((current) =>
        current?.stepId === stepId && current.automationId === automationId
          ? current
          : { key, automationId, stepId, resumeConversationId }
      );
      store.dispatch(actions.setFocusStep({ step: stepId, highlight: false }));
    },
    [canOpen, automationId, store]
  );
  const close = useCallback(() => setSession(null), []);

  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const savingRef = useRef(false);
  const [saving, setSaving] = useState(false);

  const beforePersist = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: AUTOMATIONS.AUTOMATION(store.getState().automation.id),
    });
  }, [queryClient, store]);

  const notify = useCallback(
    (outcome: SaveFirstOutcome) => {
      const toasts: Partial<
        Record<SaveFirstOutcome, { variant: unknown; message: string }>
      > = {
        unresolved: {
          variant: toastVariant.SUCCESS,
          message: t('Saved. Reopen the step to build with AI.'),
        },
        timeout: {
          variant: toastVariant.WARNING,
          message: t(
            'The save is taking longer than expected. Reopen the step once it finishes.'
          ),
        },
        busy: {
          variant: toastVariant.WARNING,
          message: t('Saving in progress. Try again in a moment.'),
        },
      };
      const toast = toasts[outcome];
      if (toast) store.dispatch(actions.toast(toast));
    },
    [store, t]
  );

  const saveThenOpen = useCallback(
    async ({ stepId, confirm }: SaveThenOpenRequest) => {
      if (!canOpen || !automationId) return null;
      if (savingRef.current || store.getState().persisting !== 0) {
        notify('busy');
        return 'busy';
      }
      savingRef.current = true;
      setSaving(true);
      try {
        const outcome = await saveFirstWithStore({
          store,
          stepId,
          confirm,
          beforePersist,
          open,
          confirmTimeoutMs: CONFIRM_TIMEOUT_MS,
          saveTimeoutMs: SAVE_FIRST_TIMEOUT_MS,
        });
        notify(outcome);
        return outcome;
      } finally {
        savingRef.current = false;
        setSaving(false);
      }
    },
    [canOpen, automationId, store, beforePersist, open, notify]
  );

  const visible = enabled ? session : null;
  const activeStepId = visible?.stepId ?? null;

  const api = useMemo(
    () => ({ canOpen, saveThenOpen, saving, activeStepId }),
    [canOpen, saveThenOpen, saving, activeStepId]
  );
  const panel = useMemo(
    () =>
      visible ? (
        <CodeStepAiPanel
          key={visible.key}
          context={{
            automationId: visible.automationId,
            stepId: visible.stepId,
          }}
          store={store}
          resumeConversationId={visible.resumeConversationId}
          beforePersist={beforePersist}
          onClose={close}
        />
      ) : null,
    [visible, store, beforePersist, close]
  );

  const panelOpen = panel !== null;
  const contentRef = useRef<HTMLDivElement>(null);
  const [railWidth, setRailWidth] = useState(0);
  useLayoutEffect(() => {
    if (!panelOpen) return undefined;
    const rail =
      contentRef.current?.parentElement?.querySelector<HTMLElement>(
        PANEL_RAIL_SELECTOR
      );
    if (!rail) return undefined;
    const update = () => setRailWidth(rail.offsetWidth);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(rail);
    return () => observer.disconnect();
  }, [panelOpen]);

  return (
    <CodeStepAiContext.Provider value={api}>
      <PiChatSideLayout panel={panel}>
        <div
          ref={contentRef}
          style={panelOpen ? { paddingRight: railWidth } : undefined}
        >
          {children}
        </div>
      </PiChatSideLayout>
    </CodeStepAiContext.Provider>
  );
};
